<script setup>
import BrandButton from "@/Components/Brand/BrandButton.vue";
</script>

<template>
    <section class="pt-8 md:px-5">
        <!--
            Mobile (Figma 1-817): full-bleed, square-cornered panel that sizes
            to its content — photo strip, copy, photo strip — with the warm
            radial sitting above the top-right corner so the orange reads at
            the top. The desktop radial (warm centred above the wide box) only
            works on wider viewports, so it's applied from md up.
        -->
        <div
            class="relative mx-auto flex max-w-[1472px] flex-col items-center justify-center overflow-hidden bg-[radial-gradient(520px_700px_at_86%_-13%,#ffba42_0%,#fc9040_25%,#f9673e_50%,#f53d3b_75%,#f4293a_87.5%,#f2143a_100%)] px-4 pb-[245px] pt-[242px] text-center text-white md:min-h-[600px] md:rounded-t-brand md:bg-[radial-gradient(73%_74%_at_50%_-22%,#ffba42_0%,#fc9040_25%,#f9673e_50%,#f53d3b_75%,#f4293a_87.5%,#f2143a_100%)] md:px-8 md:py-16 lg:min-h-[760px]"
        >
            <!--
                Perimeter photo collage. Flat Figma exports (rotation + clipping
                baked in) bled off the left/right edges and clipped by the
                rounded container. Sized taller than the module (an absolutely
                positioned img ignores `bottom` when its height is auto, so they
                used to stop short below ~1100px) and clipped by it, so the
                exports' flat bottom edge always sits below the fold; object-cover
                crops the inner edge.
            -->
            <img
                loading="lazy"
                :src="asset('images/brand/closer-collage-left.webp')"
                alt=""
                aria-hidden="true"
                class="pointer-events-none absolute left-0 top-[-52px] z-[1] block h-[calc(100%+92px)] w-[35.5%] select-none object-cover object-left-top max-md:hidden"
            />
            <img
                loading="lazy"
                :src="asset('images/brand/closer-collage-right.webp')"
                alt=""
                aria-hidden="true"
                class="pointer-events-none absolute right-0 top-0 z-[1] block h-[calc(100%+40px)] w-[28.4%] select-none object-cover object-right-top max-md:hidden"
            />

            <!--
                Mobile: the perimeter collages are replaced by photo bands
                hugging the top and bottom edges (43px / 48px in from the panel,
                Figma 1-825 / 1-819). The panel's vertical padding reserves
                their height plus the gap to the copy.
            -->
            <img
                loading="lazy"
                :src="asset('images/brand/closer-strip-top.webp')"
                alt=""
                aria-hidden="true"
                class="pointer-events-none absolute inset-x-0 top-[43px] z-[1] w-full select-none md:hidden"
            />
            <img
                loading="lazy"
                :src="asset('images/brand/closer-strip-bottom.webp')"
                alt=""
                aria-hidden="true"
                class="pointer-events-none absolute inset-x-0 bottom-[48px] z-[1] w-full select-none md:hidden"
            />

            <!-- GIFT HERO watermark behind the headline (opacity baked into the export). -->
            <img
                loading="lazy"
                :src="asset('images/brand/closer-wordmark.webp')"
                alt=""
                aria-hidden="true"
                class="pointer-events-none absolute left-0 top-[28%] z-0 block w-full -translate-y-1/2 select-none max-md:hidden"
            />

            <!--
                The perimeter collages are percentage-width, so the centre
                column has to scale with them. Mobile follows Figma 1-831/836:
                the headline scales with the viewport (88px at 393, two lines
                nearly edge to edge), 18px body on a 329px measure, 44px white
                button. From md up the type and copy width are viewport-relative
                so the headline never outgrows the gap between the two collages.
            -->
            <div class="relative z-10">
                <p
                    class="font-brand-display text-[1.5rem] font-semibold italic uppercase tracking-[-0.02em] text-[#ece6e4] md:text-[clamp(1.25rem,2.6vw,30px)]"
                >
                    Gifting Made Easy for the
                </p>
                <h2
                    class="mt-1 font-brand-display text-[clamp(3.5rem,22vw,88px)] font-black italic uppercase leading-[0.85] tracking-[-0.02em] md:text-[clamp(3.5rem,8vw,108px)]"
                >
                    Ultimate Hero
                </h2>
                <p
                    class="mx-auto mt-6 max-w-[329px] font-brand text-[18px] font-medium leading-[25px] text-white md:max-w-[min(559px,38vw)] md:text-[clamp(1.05rem,1.9vw,26px)] md:leading-[1.3]"
                >
                    Join the thousands of others who know the secret to simpler
                    wish lists.
                </p>
                <BrandButton
                    :href="route('register')"
                    variant="whiteOnRed"
                    class="mt-8 h-11 px-8 text-[15px] md:h-auto md:px-9 md:py-4 md:text-[17px]"
                >
                    <span class="md:hidden">Start a Wish List</span>
                    <span class="max-md:hidden">Create a Wish List</span>
                </BrandButton>
            </div>
        </div>
    </section>
</template>
